<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { mediaUploadApi, mediaUploadError, uploadMediaChunks } from 'dashboard/helper/mediaChunkUpload';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({ task: { type: Object, required: true } });
const emit = defineEmits(['complete', 'cancel']);
const mode = ref(props.task.file.file.size > props.task.capabilities.video_bytes ? 'document' : 'video');
const busy = ref(false);
const error = ref('');
const progress = ref(0);
const stage = ref('ready');
const uploadId = ref(null);
let controller;
let disposed = false;
const api = mediaUploadApi(props.task.origin.accountId, props.task.origin.conversationId);
const file = props.task.file.file;
const preview = URL.createObjectURL(file);
const limit = computed(() => mode.value === 'video' ? props.task.capabilities.video_bytes : props.task.capabilities.document_bytes);
const overLimit = computed(() => file.size > limit.value);
const mb = bytes => `${(bytes / 1000000).toFixed(1)} MB`;

async function start() {
  if (busy.value || overLimit.value) return;
  busy.value = true; error.value = ''; controller = new AbortController();
  try {
    const blob = await uploadMediaChunks({ api, file, mode: mode.value, requestId: props.task.id, uploadId: uploadId.value,
      signal: controller.signal, onCreated: id => { uploadId.value = id; },
      onProgress: value => { stage.value = value.stage; progress.value = Math.floor(value.received / value.total * 100); },
    });
    if (!disposed) emit('complete', { task: props.task, blob });
  } catch (failure) {
    if (!disposed && failure.name !== 'AbortError' && failure.code !== 'ERR_CANCELED') error.value = mediaUploadError(failure);
  } finally { busy.value = false; }
}
function cancel() {
  controller?.abort();
  if (uploadId.value) api.cancel(uploadId.value).catch(() => {});
  emit('cancel', props.task.id);
}
onBeforeUnmount(() => { disposed = true; controller?.abort(); URL.revokeObjectURL(preview); });
</script>

<template>
  <div class="rounded-lg bg-n-slate-2 border border-n-weak p-3 flex flex-col gap-2" role="group" aria-label="视频上传">
    <div class="flex gap-3 items-start">
      <video :src="preview" controls preload="metadata" class="w-32 max-h-24 rounded" />
      <div class="min-w-0 flex-1">
        <p class="text-sm break-all">{{ file.name }}</p>
        <p class="text-xs text-n-slate-11">{{ mb(file.size) }}</p>
        <label class="text-xs">发送方式
          <select v-model="mode" :disabled="busy || !!uploadId" class="block mt-1 max-w-full rounded border border-n-weak bg-n-background p-1">
            <option value="video">视频（可直接播放）</option>
            <option value="document">文件（保留原视频）</option>
          </select>
        </label>
      </div>
    </div>
    <p class="text-xs text-n-slate-11">当前上限 {{ mb(limit) }}。{{ mode === 'document' ? '对方可能需要下载后观看。' : '不兼容的编码会转换为可播放格式。' }}</p>
    <p v-if="overLimit" class="text-xs text-n-ruby-11">超出此发送方式的上限，请选择文件方式或先压缩视频。</p>
    <p v-if="error" role="alert" class="text-xs text-n-ruby-11">{{ error }}</p>
    <div v-if="busy" class="flex items-center gap-2 text-xs">
      <progress :value="progress" max="100" class="flex-1" />
      <span>{{ stage === 'verifying' ? '正在校验…' : `上传 ${progress}%` }}</span>
    </div>
    <div class="flex gap-2">
      <Button size="sm" :disabled="busy || overLimit" @click="start">{{ error ? '继续上传' : '上传视频' }}</Button>
      <Button size="sm" ghost slate @click="cancel">取消</Button>
    </div>
  </div>
</template>
